import { Link } from "@tanstack/react-router";
import { type CSSProperties, useEffect, useRef, useState } from "react";
import { analysisLabel } from "../lib/analysis/analyse.ts";
import { formatBytes, formatPercent } from "../lib/format.ts";
import {
  dismiss,
  dismissSettled,
  holdDismissal,
  retry,
  type Upload,
  useUploads,
} from "../lib/uploads.ts";
import { CopyButton } from "./CopyButton.tsx";
import { FileBadge } from "./FileBadge.tsx";
import { Icon } from "./Icon.tsx";
import styles from "./UploadTray.module.css";
import ui from "./ui.module.css";

const isSettled = (upload: Upload) => upload.phase === "done" || upload.phase === "failed";

const statusOf = (upload: Upload) => {
  const total = upload.file.size;
  if (upload.analysing) return analysisLabel(upload.file) ?? "";
  switch (upload.phase) {
    case "queued":
      return `Waiting · ${formatBytes(total)}`;
    case "preparing":
      return `Preparing · ${formatBytes(total)}`;
    case "uploading":
      return `${formatBytes(upload.loaded)} of ${formatBytes(total)} · ${formatPercent(upload.loaded / total)}`;
    case "done":
      return upload.result
        ? `Version ${upload.result.version.version} · ${formatBytes(total)}`
        : "Uploaded";
    case "failed":
      return upload.error ?? "The upload failed.";
  }
};

const summaryOf = (items: readonly Upload[]) => {
  const active = items.filter((u) => !isSettled(u)).length;
  const failed = items.filter((u) => u.phase === "failed").length;
  if (active > 0) {
    const done = items.length - active;
    if (done === 0)
      return items.length === 1 ? "Uploading 1 file" : `Uploading ${items.length} files`;
    return `${done} of ${items.length} uploaded`;
  }
  if (failed > 0) return failed === 1 ? "1 upload failed" : `${failed} uploads failed`;
  return items.length === 1 ? "Upload complete" : `${items.length} uploads complete`;
};

const overallProgress = (items: readonly Upload[]) => {
  const counted = items.filter((u) => u.phase !== "failed");
  const total = counted.reduce((sum, u) => sum + u.file.size, 0);
  if (total === 0) return 1;
  const loaded = counted.reduce((sum, u) => sum + (u.phase === "done" ? u.file.size : u.loaded), 0);
  return loaded / total;
};

export function UploadTray() {
  const uploads = useUploads();
  const [collapsed, setCollapsed] = useState(false);
  const engaged = useRef({ hover: false, focus: false });

  const engage = (kind: "hover" | "focus", value: boolean) => {
    engaged.current[kind] = value;
    holdDismissal(engaged.current.hover || engaged.current.focus);
  };

  const empty = uploads.length === 0;
  useEffect(() => {
    if (!empty) return;
    engaged.current = { hover: false, focus: false };
    holdDismissal(false);
  }, [empty]);

  if (empty) return null;

  const settled = uploads.some(isSettled);
  const running = uploads.some((upload) => !isSettled(upload));
  const progress = overallProgress(uploads);

  return (
    <section
      className={styles.tray}
      aria-label="Uploads"
      data-collapsed={collapsed || undefined}
      onMouseEnter={() => engage("hover", true)}
      onMouseLeave={() => engage("hover", false)}
      onFocus={() => engage("focus", true)}
      onBlur={() => engage("focus", false)}
    >
      <header className={styles.header}>
        <div className={styles.headline}>
          <h2 className={styles.summary} aria-live="polite">
            {summaryOf(uploads)}
          </h2>
          {collapsed && running ? (
            <p className={styles.overallPercent}>{formatPercent(progress)}</p>
          ) : null}
        </div>
        {settled ? (
          <button type="button" className={ui.button} data-variant="ghost" onClick={dismissSettled}>
            Clear
          </button>
        ) : null}
        <button
          type="button"
          className={ui.iconButton}
          data-variant="ghost"
          aria-expanded={!collapsed}
          aria-controls="upload-tray-list"
          aria-label={collapsed ? "Show each upload" : "Hide each upload"}
          title={collapsed ? "Show each upload" : "Hide each upload"}
          onClick={() => setCollapsed(!collapsed)}
        >
          <Icon name="chevron" className={styles.toggleArrow} />
        </button>
      </header>
      <ul id="upload-tray-list" className={styles.list} hidden={collapsed}>
        {uploads.map((upload) => {
          const rowProgress =
            upload.phase === "done"
              ? 1
              : upload.phase === "uploading"
                ? upload.loaded / upload.file.size
                : 0;
          const nodeId =
            upload.result?.node.id ??
            (upload.target.kind === "version" ? upload.target.nodeId : null);
          const settledRow = isSettled(upload);
          return (
            <li
              key={upload.id}
              className={styles.item}
              data-phase={upload.phase}
              data-analysing={upload.analysing || undefined}
            >
              <span className={styles.sticker}>
                <FileBadge contentType={upload.file.type || null} name={upload.file.name} />
                {upload.phase === "done" ? (
                  <span className={styles.seal} data-tone="done">
                    <Icon name="check" />
                  </span>
                ) : null}
                {upload.phase === "failed" ? (
                  <span className={styles.seal} data-tone="failed" aria-hidden="true">
                    !
                  </span>
                ) : null}
              </span>
              <div className={styles.text}>
                <p className={styles.name}>
                  {upload.phase === "done" && nodeId ? (
                    <Link to="/files/$id" params={{ id: nodeId }} className={styles.nameLink}>
                      {upload.file.name}
                    </Link>
                  ) : (
                    upload.file.name
                  )}
                </p>
                <p className={styles.target}>
                  {upload.target.kind === "version"
                    ? `New version of ${upload.target.name}`
                    : "New file"}
                </p>
              </div>
              <div className={styles.actions}>
                {upload.phase === "done" && upload.result ? (
                  <CopyButton
                    value={upload.result.node.url}
                    label={`Copy link to ${upload.file.name}`}
                    compact
                  />
                ) : null}
                {upload.phase === "failed" && upload.retryable ? (
                  <button
                    type="button"
                    className={ui.iconButton}
                    onClick={() => retry(upload.id)}
                    aria-label={`Retry ${upload.file.name}`}
                    title="Retry"
                  >
                    <Icon name="retry" />
                  </button>
                ) : null}
                <button
                  type="button"
                  className={ui.iconButton}
                  data-variant="ghost"
                  onClick={() => dismiss(upload.id)}
                  aria-label={
                    settledRow ? `Dismiss ${upload.file.name}` : `Cancel ${upload.file.name}`
                  }
                  title={settledRow ? "Dismiss" : "Cancel"}
                >
                  <Icon name="close" />
                </button>
              </div>
              <div
                className={styles.bar}
                role="progressbar"
                aria-label={`${upload.file.name} progress`}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(rowProgress * 100)}
              >
                <span style={{ "--progress": rowProgress } as CSSProperties} />
              </div>
              <p className={styles.status} role={upload.phase === "failed" ? "alert" : undefined}>
                {statusOf(upload)}
              </p>
            </li>
          );
        })}
      </ul>
      {collapsed && running ? (
        <div
          className={styles.overallBar}
          role="progressbar"
          aria-label="All uploads"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
        >
          <span style={{ "--progress": progress } as CSSProperties} />
        </div>
      ) : null}
    </section>
  );
}
